import { useState, useEffect, useRef } from 'react'
import { useForecast } from '../../hooks/useForecast'
import { C, F } from './theme'
import { Card, SectionHead, Pill, Btn, Head, Icon, LockedCard, gbp } from './prims'
import { deriveModel, hasProposedSlot } from './derive'
import { useMe } from '../../hooks/useMe'

// EM-6 · Model & scenarios — the economics core of Forecast Studio. A live
// sandbox (attendance / ticket yield / cost adjustment) drives an incremental
// P&L that recomputes instantly via deriveModel(). Slider changes save with a
// 400ms debounce. All money is integer PENCE; format at the edge with gbp().

type Scenario = { att: number; yieldPence: number; costAdj: number }

function Slider({ label, value, min, max, step, onChange, fmt, base, disabled, hint }: {
  label: string
  value: number
  min: number
  max: number
  step: number
  onChange: (v: number) => void
  fmt: (v: number) => string
  base: number | null
  disabled?: boolean
  hint?: string
}) {
  const changed = value !== base
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 700, color: C.inkSoft }}>{label}</span>
        <span style={{ fontFamily: F.mono, fontSize: 13, fontWeight: 700, color: changed ? C.green : C.ink }}>{fmt(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: '100%', accentColor: C.green, opacity: disabled ? 0.4 : 1 }}
      />
      {hint && <div style={{ fontFamily: F.body, fontSize: 11, color: C.inkFaint, marginTop: 4 }}>{hint}</div>}
    </div>
  )
}

function PLRow({ label, valuePence, bold, big, negative }: {
  label: string
  valuePence: number
  bold?: boolean
  big?: boolean
  negative?: boolean
}) {
  const signed = negative ? -valuePence : valuePence
  const valColor = big ? (signed >= 0 ? C.green : C.oxblood) : C.ink
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      padding: bold ? '9px 0 0' : '5px 0',
      borderTop: bold ? `1px solid ${C.lineSoft}` : undefined,
      marginTop: bold ? 4 : 0
    }}>
      <span style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: bold ? 800 : 500, color: bold ? C.ink : C.inkSoft }}>{label}</span>
      <span style={{
        fontFamily: big ? F.serif : F.mono,
        fontSize: big ? 20 : 13,
        fontWeight: bold ? 800 : 500,
        color: valColor,
        whiteSpace: 'nowrap'
      }}>
        {negative ? `− ${gbp(valuePence)}` : gbp(valuePence)}
      </span>
    </div>
  )
}

const DEMAND_BARS = [72, 80, 88, 96, 90]
const RESOURCES = [
  { name: 'Steam locos' },
  { name: 'Diesel locos' },
  { name: 'Carriage sets' },
  { name: 'Train crew' }
]

export function ModelTab({ eventId }: { eventId: number }) {
  const { fc, save } = useForecast(eventId)
  const { data: me } = useMe()

  const [local, setLocal] = useState<Scenario>({ att: 0, yieldPence: 0, costAdj: 0 })
  const [attFcInput, setAttFcInput] = useState('')   // baseline attendance (people)
  const [yieldFcInput, setYieldFcInput] = useState('') // baseline yield (pounds)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const baseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Seed local scenario ONCE per idea (the tab remounts on tab-switch, so this
  // re-reads the latest persisted scenario each time it's opened). Re-seeding on
  // every fc mutation would let an in-flight save's server echo snap a slider
  // back over the value the planner is currently dragging.
  const seededRef = useRef<number | null>(null)
  useEffect(() => {
    if (!fc) return
    if (seededRef.current === eventId) return
    seededRef.current = eventId
    setLocal({
      att: fc.scenario.att ?? fc.attendanceFc ?? 0,
      yieldPence: fc.scenario.yieldPence ?? fc.ticketYieldPence ?? 0,
      costAdj: fc.scenario.costAdj ?? 0
    })
    setAttFcInput(fc.attendanceFc != null ? String(fc.attendanceFc) : '')
    setYieldFcInput(fc.ticketYieldPence != null ? String(fc.ticketYieldPence / 100) : '')
  }, [fc, eventId])

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); if (baseTimer.current) clearTimeout(baseTimer.current) }, [])

  if (!me?.hasFinancial) return <LockedCard label="The model needs financial access" />
  if (!fc) return null

  const slotted = hasProposedSlot(fc)
  const d = deriveModel(fc, local)

  const pushScenario = (next: Scenario) => {
    setLocal(next)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      void save({ scenario: next }).catch(() => { /* never throw on API error */ })
    }, 400)
  }

  const setAtt = (att: number) => pushScenario({ ...local, att })
  const setYield = (yieldPence: number) => pushScenario({ ...local, yieldPence })
  const setCostAdj = (costAdj: number) => pushScenario({ ...local, costAdj })

  // Baseline anchor (attendanceFc / ticketYieldPence) — what the sliders flex
  // around. Persisted separately (own debounce) so a baseline edit and a slider
  // drag don't cancel each other. Nudge the sandbox off 0 when first set.
  const saveBaseline = (attendanceFc: number | null, ticketYieldPence: number | null) => {
    if (baseTimer.current) clearTimeout(baseTimer.current)
    baseTimer.current = setTimeout(() => { void save({ attendanceFc, ticketYieldPence }).catch(() => { /* swallow */ }) }, 400)
  }
  const onBaseAtt = (v: string) => {
    setAttFcInput(v)
    const n = v.trim() === '' ? null : Math.max(0, Math.round(Number(v) || 0))
    saveBaseline(n, fc.ticketYieldPence)
    if ((local.att ?? 0) === 0 && n) setLocal((l) => ({ ...l, att: n }))
  }
  const onBaseYield = (v: string) => {
    setYieldFcInput(v)
    const p = v.trim() === '' ? null : Math.max(0, Math.round((Number(v) || 0) * 100))
    saveBaseline(fc.attendanceFc, p)
    if ((local.yieldPence ?? 0) === 0 && p) setLocal((l) => ({ ...l, yieldPence: p }))
  }

  const reset = () => {
    const base: Scenario = { att: fc.attendanceFc ?? 0, yieldPence: fc.ticketYieldPence ?? 0, costAdj: 0 }
    setLocal(base)
    if (timer.current) clearTimeout(timer.current)
    void save({ scenario: base }).catch(() => { /* swallow */ })
  }

  const attFc = fc.attendanceFc ?? 0
  const yieldFc = fc.ticketYieldPence ?? 0
  // Slider ranges: a band around the baseline, but with absolute floors so the
  // control is always draggable even before a baseline is entered.
  const attMax = Math.max(Math.round(attFc * 1.4), Math.round((local.att ?? 0) * 1.2), 2000)
  const yieldMax = Math.max(Math.round(yieldFc * 1.4), Math.round((local.yieldPence ?? 0) * 1.2), 5000)
  const hasCosts = d.baseCostsPence > 0
  const posInc = d.incrementalPence >= 0

  const headStat = (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontFamily: F.body, fontSize: 10.5, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: C.brass }}>Net incremental</div>
      <div style={{ fontFamily: F.serif, fontSize: 26, fontWeight: 800, color: posInc ? C.green : C.oxblood, lineHeight: 1.05, marginTop: 2 }}>{gbp(d.incrementalPence)}</div>
    </div>
  )

  return (
    <div>
      <Head eyebrow="Model · scenario" title="Model & scenarios" right={headStat} />

      {!slotted && (
        <Card style={{ marginBottom: 18, borderColor: `${C.amber}55`, background: C.amberSoft }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <Icon name="calendar" size={18} color={C.amber} />
            <div>
              <div style={{ fontFamily: F.body, fontSize: 13.5, fontWeight: 800, color: C.ink }}>Set a proposed slot first</div>
              <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft, marginTop: 3 }}>
                The headline is blocked until a proposed slot is chosen — the incremental figure depends on the running days it displaces. You can still explore the sandbox below.
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Row 1 — sandbox + incremental P&L */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 16, marginBottom: 16 }}>
        {/* A — Scenario sandbox */}
        <Card>
          <SectionHead
            icon="bolt"
            label="Scenario sandbox"
            right={<Btn variant="ghost" small icon="refresh" onClick={reset}>Reset</Btn>}
          />
          {/* Baseline anchor — the sliders flex around these. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
            <label style={{ display: 'block' }}>
              <span style={{ display: 'block', fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.inkSoft, marginBottom: 5 }}>Baseline attendance</span>
              <input inputMode="numeric" value={attFcInput} onChange={(e) => onBaseAtt(e.target.value)} placeholder="e.g. 8000"
                style={{ width: '100%', padding: '7px 9px', border: `1px solid ${C.line}`, borderRadius: 7, fontFamily: F.mono, fontSize: 12.5, color: C.ink, boxSizing: 'border-box' }} />
            </label>
            <label style={{ display: 'block' }}>
              <span style={{ display: 'block', fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.inkSoft, marginBottom: 5 }}>Baseline ticket yield (£)</span>
              <input inputMode="decimal" value={yieldFcInput} onChange={(e) => onBaseYield(e.target.value)} placeholder="e.g. 24.00"
                style={{ width: '100%', padding: '7px 9px', border: `1px solid ${C.line}`, borderRadius: 7, fontFamily: F.mono, fontSize: 12.5, color: C.ink, boxSizing: 'border-box' }} />
            </label>
          </div>
          <Slider
            label="Attendance"
            value={local.att}
            min={0}
            max={attMax}
            step={50}
            onChange={setAtt}
            fmt={(v) => v.toLocaleString('en-GB')}
            base={fc.attendanceFc}
          />
          <Slider
            label="Ticket yield"
            value={local.yieldPence}
            min={0}
            max={yieldMax}
            step={50}
            onChange={setYield}
            fmt={gbp}
            base={fc.ticketYieldPence}
          />
          <Slider
            label="Cost adjustment"
            value={local.costAdj}
            min={-20}
            max={40}
            step={1}
            onChange={setCostAdj}
            fmt={(v) => (v > 0 ? '+' : '') + v + '%'}
            base={0}
            disabled={!hasCosts}
            hint={hasCosts ? undefined : 'Add cost lines (Costs & revenue tab) or a contract fee to model cost changes.'}
          />
          <div style={{
            marginTop: 4,
            padding: '12px 14px',
            borderRadius: 10,
            background: posInc ? C.greenBg : C.oxbloodSoft,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12
          }}>
            <span style={{ fontFamily: F.body, fontSize: 12, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: posInc ? C.green : C.oxblood }}>
              Net incremental
            </span>
            <span style={{ fontFamily: F.serif, fontSize: 22, fontWeight: 800, color: posInc ? C.green : C.oxblood }}>
              {slotted ? gbp(d.incrementalPence) : '—'}
            </span>
          </div>
        </Card>

        {/* B — Incremental P&L */}
        <Card>
          <SectionHead icon="pound" label="Incremental P&L" />
          <PLRow label="Ticket revenue" valuePence={d.ticketRevPence} />
          {d.ancillaryPence > 0 && <PLRow label="Other revenue" valuePence={d.ancillaryPence} />}
          <PLRow label="Gross revenue" valuePence={d.grossRevPence} bold />
          <PLRow label={d.costAdj !== 0 ? `− Total costs (${d.costAdj > 0 ? '+' : ''}${d.costAdj}% adj)` : '− Total costs'} valuePence={d.totalCostsPence} negative />
          <PLRow label="Event profit" valuePence={d.eventProfitPence} bold />
          <PLRow label={`− Normal running days (${d.displacedCount})`} valuePence={d.normalDaysPence} negative />
          <PLRow label="Net incremental" valuePence={d.incrementalPence} bold big />
        </Card>
      </div>

      {/* Row 2 — demand curve + resource impact */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 16 }}>
        {/* C — Projected demand */}
        <Card>
          <SectionHead icon="trend" label="Projected demand" />
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4%', height: 120, marginBottom: 12 }}>
            {DEMAND_BARS.map((v, i) => {
              const thisEvent = i >= 3
              return (
                <div key={i} style={{
                  width: '18%',
                  height: `${v}%`,
                  borderRadius: '6px 6px 0 0',
                  background: thisEvent ? C.green : C.brassLt,
                  opacity: thisEvent ? 1 : 0.6
                }} />
              )
            })}
          </div>
          <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft }}>
            Curve modelled from linked baselines + research seasonality.
          </div>
          <div style={{ fontFamily: F.mono, fontSize: 11.5, color: C.inkFaint, marginTop: 6 }}>
            No live bookings yet — 14 months out.
          </div>
        </Card>

        {/* D — Resource impact & clash */}
        <Card>
          <SectionHead icon="wrench" label="Resource impact & clash" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {RESOURCES.map((r) => (
              <div key={r.name} style={{
                display: 'flex', alignItems: 'center', gap: 9,
                padding: '9px 12px', borderRadius: 9,
                background: C.paper, border: `1px solid ${C.lineSoft}`
              }}>
                <Icon name="train" size={15} color={C.inkFaint} />
                <span style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 700, color: C.inkSoft }}>{r.name}</span>
                <span style={{ fontFamily: F.body, fontSize: 11.5, color: C.inkFaint, marginLeft: 'auto' }}>· plan in Studio</span>
              </div>
            ))}
          </div>
          <Pill fg={C.oxblood} bg={C.oxbloodSoft} icon="alert">Clash check runs against the committed plan on commit.</Pill>
        </Card>
      </div>
    </div>
  )
}
